import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Definindo a interface de props para o componente
interface StartScreenProps {
  onStart?: () => void;
}

const StartScreenContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #121212 0%, #1a1a1a 100%);
  position: relative;
  overflow: hidden;
`;

const GridBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(255, 144, 82, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 144, 82, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  z-index: 1;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
  padding: 2rem;
  max-width: 1200px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 4rem;
  font-weight: 700;
  color: var(--color-white);
  margin-bottom: 1rem;
  text-align: center;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 400;
  color: var(--color-white);
  opacity: 0.8;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 800px;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }
`;

const StartButton = styled.button`
  padding: 1rem 3rem;
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%);
  color: var(--color-white);
  border: none;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 0 20px rgba(255, 144, 82, 0.5);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(30deg);
    animation: shine 3s infinite;
  }
  
  @keyframes shine {
    0% {
      transform: translateX(-100%) rotate(30deg);
    }
    100% {
      transform: translateX(100%) rotate(30deg);
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem 2.5rem;
    font-size: 1.3rem;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  width: 100%;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 2rem;
  }
`;

const Card = styled.div`
  background: rgba(26, 26, 26, 0.7);
  border-radius: 15px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 144, 82, 0.2);
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 1.2rem;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--color-primary);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const CardContent = styled.p`
  font-size: 1rem;
  color: var(--color-white);
  opacity: 0.8;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const ProfileImage = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%);
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 4rem;
  color: var(--color-white);
  box-shadow: 0 0 20px rgba(255, 144, 82, 0.5);
  
  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    font-size: 3rem;
  }
`;

const StartScreen: React.FC<StartScreenProps> = ({ onStart }) => {
  const handleStartClick = () => {
    if (onStart) {
      onStart();
    }
  };

  return (
    <StartScreenContainer>
      <GridBackground />
      
      <ContentContainer>
        <ProfileContainer>
          <ProfileImage>
            CG
          </ProfileImage>
          
          <Title>
            Caíque Gonçalves
          </Title>
          
          <Subtitle>
            Tech Lead / Software Architect Manager / Game Designer / Game Dev
          </Subtitle>
        </ProfileContainer>
        
        {onStart ? (
          <StartButton
            onClick={handleStartClick}
          >
            Start Journey
          </StartButton>
        ) : (
          <Link to="/menu" style={{ textDecoration: 'none' }}>
            <StartButton>
              Start Journey
            </StartButton>
          </Link>
        )}
        
        <CardsContainer>
          <Card>
            <CardTitle>Game Development</CardTitle>
            <CardContent>
              Experience with Unity, Unreal Engine, C# and C++. Development of games for various platforms and genres.
            </CardContent>
          </Card>
          
          <Card>
            <CardTitle>Software Architecture</CardTitle>
            <CardContent>
              Specialist in scalable system architecture, design patterns, and performance optimization.
            </CardContent>
          </Card>
          
          <Card>
            <CardTitle>Innovative Projects</CardTitle>
            <CardContent>
              Development of creative and innovative solutions for complex challenges across various sectors.
            </CardContent>
          </Card>
          
          <Card>
            <CardTitle>Humanized Management</CardTitle>
            <CardContent>
              Leadership focused on people, promoting collaborative environments and team development.
            </CardContent>
          </Card>
        </CardsContainer>
      </ContentContainer>
    </StartScreenContainer>
  );
};

export default StartScreen;
